import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  type Table as TanstackTable,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import {
  DEFAULT_TABLE_ROWS_PER_PAGE,
  type RowsPerPageOption,
} from './dataTable.constants';

export type DataTablePaginationMode = 'off' | 'client' | 'manual';

export type DataTableSortingMode = 'client' | 'manual';

export type DataTableFilteringMode = 'client' | 'manual';

export type UseDataTableOptions<TData extends RowData> = {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  initialSorting?: SortingState;
  /**
   * Sorting value when controlled — see `onSortingChange`. Ignored for reads until that handler is passed.
   * Use {@link sortingMode} `'manual'` when the API applies sort.
   */
  sorting?: SortingState;
  /** When set, sorting is controlled: updates go through this handler; pass `sorting` for the current value. */
  onSortingChange?: OnChangeFn<SortingState>;
  /** `'client'` (default): TanStack sorts in memory. `'manual'`: you sort on the server / in `data`. */
  sortingMode?: DataTableSortingMode;
  /** Column filters: client (`getFilteredRowModel`) or {@link filteringMode} `'manual'` with pre-filtered `data`. */
  enableFiltering?: boolean;
  initialColumnFilters?: ColumnFiltersState;
  /**
   * Filter value when controlled — see `onColumnFiltersChange`.
   * Requires {@link enableFiltering}. Use {@link filteringMode} `'manual'` when `data` is already filtered.
   */
  columnFilters?: ColumnFiltersState;
  /** When set with {@link enableFiltering}, filters are controlled; pass `columnFilters` for the current value. */
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  /**
   * `'client'` (default): TanStack runs `getFilteredRowModel` on `data`.
   * `'manual'`: you filter (e.g. on the server); pass filtered `data` and set `manualFiltering: true` internally.
   */
  filteringMode?: DataTableFilteringMode;
  /**
   * @deprecated Prefer {@link paginationMode} `'client'`.
   * When true and `paginationMode` is omitted, behaves like `paginationMode: 'client'`.
   */
  enablePagination?: boolean;
  /**
   * `'off'`: no pagination row model.
   * `'client'`: TanStack slices `data` by `pagination` / `pageSize`.
   * `'manual'`: you paginate on the server — pass `pageCount`, `pagination`, and `onPaginationChange`.
   */
  paginationMode?: DataTablePaginationMode;
  /** Required when `paginationMode` is `'manual'` (total pages from the server). */
  pageCount?: number;
  /** Pagination value when controlled — see `onPaginationChange` (`paginationMode` `'client'` or `'manual'`). */
  pagination?: PaginationState;
  /** When set, pagination is controlled; pass `pagination` for the current value. */
  onPaginationChange?: OnChangeFn<PaginationState>;
  /**
   * Initial `pageSize` when pagination is on. Ignored if `initialPagination.pageSize` is set.
   * Defaults to {@link DEFAULT_TABLE_ROWS_PER_PAGE}. Prefer a value from `ROWS_PER_PAGE`.
   */
  rowsPerPage?: RowsPerPageOption;
  initialPagination?: PaginationState;
  /** Row selection state (TanStack row selection). */
  enableRowSelection?: boolean;
  initialRowSelection?: RowSelectionState;
  /** Row selection when controlled — see `onRowSelectionChange`. Requires {@link enableRowSelection}. */
  rowSelection?: RowSelectionState;
  /** When set with {@link enableRowSelection}, selection is controlled; pass `rowSelection` for the current value. */
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /**
   * Optional `getRowId` forwarded to TanStack (stable row ids for selection / updates).
   */
  getRowId?: TableOptions<TData>['getRowId'];
  /**
   * Rare escape hatch: merged **before** design-system defaults so
   * row models and managed `state` slices from this hook still win when not overridden here.
   * Do not pass `data`, `columns`, or `state` here.
   */
  tableOptions?: Partial<
    Omit<
      TableOptions<TData>,
      | 'data'
      | 'columns'
      | 'state'
      | 'getCoreRowModel'
      | 'getSortedRowModel'
      | 'getPaginationRowModel'
      | 'getFilteredRowModel'
      | 'manualFiltering'
    >
  >;
};

/** TanStack table instance returned by {@link useDataTable}. */
export type DataTableTanstackTable<TData extends RowData> =
  TanstackTable<TData>;

export type UseDataTableReturn<TData extends RowData> = {
  table: TanstackTable<TData>;
  sorting: SortingState;
  setSorting: OnChangeFn<SortingState>;
  columnFilters: ColumnFiltersState;
  setColumnFilters: OnChangeFn<ColumnFiltersState>;
  pagination: PaginationState;
  setPagination: OnChangeFn<PaginationState>;
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
};

/**
 * Standard design-system table hook: wires TanStack with shared sorting, optional
 * filtering, pagination, and row selection so feature code does not reimplement
 * row models or `autoResetAll` behavior.
 */
export function useDataTable<TData extends RowData>(
  options: UseDataTableOptions<TData>,
): UseDataTableReturn<TData> {
  const {
    data,
    columns,
    initialSorting = [],
    sorting: sortingControlled,
    onSortingChange: onSortingChangeControlled,
    sortingMode = 'client',
    enableFiltering = false,
    initialColumnFilters = [],
    columnFilters: columnFiltersControlled,
    onColumnFiltersChange: onColumnFiltersChangeControlled,
    filteringMode = 'client',
    enablePagination = false,
    paginationMode: paginationModeOption,
    pageCount,
    pagination: paginationControlled,
    onPaginationChange: onPaginationChangeControlled,
    rowsPerPage,
    initialPagination,
    enableRowSelection = false,
    initialRowSelection = {},
    rowSelection: rowSelectionControlled,
    onRowSelectionChange: onRowSelectionChangeControlled,
    getRowId,
    tableOptions,
  } = options;

  const paginationMode: DataTablePaginationMode =
    paginationModeOption ?? (enablePagination ? 'client' : 'off');

  const isSortingControlled = onSortingChangeControlled !== undefined;
  const isPaginationControlled = onPaginationChangeControlled !== undefined;
  const isColumnFiltersControlled =
    onColumnFiltersChangeControlled !== undefined;
  const isRowSelectionControlled = onRowSelectionChangeControlled !== undefined;

  if (paginationMode === 'manual' && pageCount === undefined) {
    throw new Error(
      'useDataTable: `paginationMode: "manual"` requires `pageCount` from the server.',
    );
  }
  if (isColumnFiltersControlled && !enableFiltering) {
    throw new Error(
      'useDataTable: controlled `columnFilters` requires `enableFiltering: true`.',
    );
  }
  if (isRowSelectionControlled && !enableRowSelection) {
    throw new Error(
      'useDataTable: controlled `rowSelection` requires `enableRowSelection: true`.',
    );
  }

  const [sortingInternal, setSortingInternal] =
    useState<SortingState>(initialSorting);
  const sorting =
    isSortingControlled && sortingControlled !== undefined
      ? sortingControlled
      : sortingInternal;
  const setSorting: OnChangeFn<SortingState> = isSortingControlled
    ? onSortingChangeControlled
    : setSortingInternal;

  const [paginationInternal, setPaginationInternal] = useState<PaginationState>(
    () => ({
      pageIndex: initialPagination?.pageIndex ?? 0,
      pageSize:
        initialPagination?.pageSize ??
        rowsPerPage ??
        DEFAULT_TABLE_ROWS_PER_PAGE,
    }),
  );
  const pagination =
    isPaginationControlled && paginationControlled !== undefined
      ? paginationControlled
      : paginationInternal;
  const setPagination: OnChangeFn<PaginationState> = isPaginationControlled
    ? onPaginationChangeControlled
    : setPaginationInternal;

  const [columnFiltersInternal, setColumnFiltersInternal] =
    useState<ColumnFiltersState>(initialColumnFilters);
  const columnFilters =
    isColumnFiltersControlled && columnFiltersControlled !== undefined
      ? columnFiltersControlled
      : columnFiltersInternal;
  const setColumnFilters: OnChangeFn<ColumnFiltersState> =
    isColumnFiltersControlled
      ? onColumnFiltersChangeControlled
      : setColumnFiltersInternal;

  const [rowSelectionInternal, setRowSelectionInternal] =
    useState<RowSelectionState>(initialRowSelection);
  const rowSelection =
    isRowSelectionControlled && rowSelectionControlled !== undefined
      ? rowSelectionControlled
      : rowSelectionInternal;
  const setRowSelection: OnChangeFn<RowSelectionState> =
    isRowSelectionControlled
      ? onRowSelectionChangeControlled
      : setRowSelectionInternal;

  const tableState = useMemo(
    () => ({
      sorting,
      ...(enableFiltering ? { columnFilters } : {}),
      ...(paginationMode !== 'off' ? { pagination } : {}),
      ...(enableRowSelection ? { rowSelection } : {}),
    }),
    [
      sorting,
      enableFiltering,
      columnFilters,
      paginationMode,
      pagination,
      enableRowSelection,
      rowSelection,
    ],
  );

  const table = useReactTable({
    ...(tableOptions ?? {}),
    data,
    columns,
    getRowId,
    enableRowSelection,
    autoResetAll: false,
    getCoreRowModel: getCoreRowModel(),
    ...(sortingMode === 'client'
      ? { getSortedRowModel: getSortedRowModel() }
      : { manualSorting: true }),
    ...(paginationMode === 'client'
      ? {
          manualPagination: false,
          getPaginationRowModel: getPaginationRowModel(),
          onPaginationChange: setPagination,
        }
      : {}),
    ...(paginationMode === 'manual'
      ? {
          manualPagination: true,
          pageCount: pageCount ?? 0,
          onPaginationChange: setPagination,
        }
      : {}),
    ...(enableFiltering
      ? filteringMode === 'client'
        ? {
            manualFiltering: false,
            getFilteredRowModel: getFilteredRowModel(),
            onColumnFiltersChange: setColumnFilters,
          }
        : {
            manualFiltering: true,
            onColumnFiltersChange: setColumnFilters,
          }
      : {}),
    onSortingChange: setSorting,
    ...(enableRowSelection ? { onRowSelectionChange: setRowSelection } : {}),
    state: tableState,
  });

  return {
    table,
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters,
    pagination,
    setPagination,
    rowSelection,
    setRowSelection,
  };
}

/**
 * For custom `useReactTable` wrappers that cannot use {@link useDataTable} yet —
 * spread these first, then add feature-specific row models and state.
 */
export function createBaseDataTableOptions<TData extends RowData>(): Pick<
  TableOptions<TData>,
  'getCoreRowModel' | 'getSortedRowModel' | 'autoResetAll'
> {
  return {
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetAll: false,
  };
}

export type {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  Row,
  RowData,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table';
